
import axios, { AxiosResponse } from 'axios';
import {
  MerchantProfileListResponse,
  DeleteMerchantProfileResponse,
  ClearDataResponse,
  CreateMerchantAdminRequest,
  MerchantListResponse,
  DeleteMerchantResponse,
  MerchantProfile,
  Merchant,
  CreateMerchantProfileRequest,
  CreateMerchantProfileResponse
} from './types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const merchantApi = {
  // Main ProPay API endpoints
  createMerchantProfile: async (
    request: CreateMerchantProfileRequest,
    billerId: string,
    authToken: string
  ): Promise<CreateMerchantProfileResponse> => {
    const authHeader = `Basic ${btoa(`${billerId}:${authToken}`)}`;
    const response: AxiosResponse<CreateMerchantProfileResponse> = await apiClient.put(
      '/protectpay/merchantprofiles/',
      request,
      {
        headers: {
          Authorization: authHeader,
        },
      }
    );
    return response.data;
  },

  deleteMerchantProfile: async (profileId: number): Promise<DeleteMerchantProfileResponse> => {
    const response: AxiosResponse<DeleteMerchantProfileResponse> = await apiClient.delete(
      `/protectpay/merchantprofiles/${profileId}/`
    );
    return response.data;
  },

  // Admin endpoints for merchant profiles
  getAllMerchantProfiles: async (): Promise<MerchantProfileListResponse> => {
    const response: AxiosResponse<MerchantProfileListResponse> = await apiClient.get(
      '/protectpay/admin/merchantprofiles/list'
    );
    return response.data;
  },

  clearAllMerchantData: async (): Promise<ClearDataResponse> => {
    const response: AxiosResponse<ClearDataResponse> = await apiClient.delete(
      '/protectpay/admin/merchantprofiles/clear'
    );
    return response.data;
  },

  // Partner relationship methods
  getMerchantsByPartner: async (partnerId: string): Promise<MerchantProfileListResponse> => {
    const response: AxiosResponse<MerchantProfileListResponse> = await apiClient.get(
      `/protectpay/admin/merchantprofiles/partner/${partnerId}`
    );
    return response.data;
  },

  assignMerchantToPartner: async (profileId: number, partnerId: string): Promise<MerchantProfile> => {
    const response: AxiosResponse<MerchantProfile> = await apiClient.patch(
      `/protectpay/admin/merchantprofiles/${profileId}/partner`,
      { partnerId }
    );
    return response.data;
  },

  removeMerchantFromPartner: async (profileId: number): Promise<MerchantProfile> => {
    const response: AxiosResponse<MerchantProfile> = await apiClient.delete(
      `/protectpay/admin/merchantprofiles/${profileId}/partner`
    );
    return response.data;
  },

  getMerchantProfile: async (profileId: number): Promise<MerchantProfile> => {
    const response: AxiosResponse<MerchantProfile> = await apiClient.get(
      `/protectpay/admin/merchantprofiles/${profileId}`
    );
    return response.data;
  },



  // Admin endpoints for merchants
  createMerchantAdmin: async (request: CreateMerchantAdminRequest): Promise<string> => {
    const response: AxiosResponse<void> = await apiClient.post(
      '/protectpay/admin/merchants',
      request
    );
    // Extract UUID from Location header
    const location = response.headers.location || response.headers.Location;
    return location || '';
  },

  getAllMerchantsAdmin: async (partnerId?: string): Promise<MerchantListResponse> => {
    const url = partnerId
      ? `/protectpay/admin/merchants/list?partnerId=${partnerId}`
      : '/protectpay/admin/merchants/list';
    const response: AxiosResponse<MerchantListResponse> = await apiClient.get(url);
    return response.data;
  },

  deleteMerchantAdmin: async (merchantId: string): Promise<DeleteMerchantResponse> => {
    const response: AxiosResponse<DeleteMerchantResponse> = await apiClient.delete(
      `/protectpay/admin/merchants/${merchantId}`
    );
    return response.data;
  },

  // Legacy methods for backwards compatibility
  getAllMerchants: async (): Promise<MerchantProfileListResponse> => {
    return merchantApi.getAllMerchantProfiles();
  },

  clearAllMerchants: async (): Promise<ClearDataResponse> => {
    return merchantApi.clearAllMerchantData();
  },
};
